import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  TextField,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CloudDownload as ImportIcon,
  Preview as PreviewIcon,
  Refresh as RefreshIcon,
  Assessment as StatsIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const HCPJobImportManager = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30 days ago for broader default range
  const [endDate, setEndDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // 7 days in the future
  const [importResults, setImportResults] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importStats, setImportStats] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  useEffect(() => {
    if (open) {
      fetchImportStats();
    }
  }, [open]); // fetchImportStats is stable, no need to add as dependency

  const fetchImportStats = async () => {
    try {
      // Get tenant ID and city code from environment or user context
      const tenantId = process.env.REACT_APP_TENANT_ID || '1';
      const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';
      
      const url = new URL(`${apiBase}/api/admin/hcp/import-status`);
      url.searchParams.append('tenantId', tenantId);
      url.searchParams.append('cityCode', cityCode);
      
      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch import statistics');
      }

      const data = await response.json();
      if (data.success) {
        setImportStats(data.stats);
      } else {
        throw new Error(data.error || 'Failed to load import statistics');
      }
    } catch (error) {
      console.error('Error fetching import stats:', error);
      setError(`Failed to load import statistics: ${error.message}`);
    }
  };

  const handlePreviewImport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (endDate < startDate) {
      setError('End date must be after or equal to start date');
      return;
    }

    // Add warning for very large date ranges
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 90) {
      const confirmLargeRange = window.confirm(
        `You've selected a ${daysDiff}-day range. Large date ranges may take longer to process and could impact performance. Do you want to continue?`
      );
      if (!confirmLargeRange) {
        return;
      }
    }

    setPreviewLoading(true);
    setError('');
    setPreviewData(null);

    try {
      // Get tenant ID and city code from environment or user context
      const tenantId = process.env.REACT_APP_TENANT_ID || '1';
      const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';
      
      const response = await fetch(`${apiBase}/api/admin/hcp/preview-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          tenantId,
          cityCode
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to preview import');
      }

      if (data.success) {
        setPreviewData(data.preview);
      } else {
        throw new Error(data.error || 'Failed to preview import');
      }
    } catch (error) {
      setError(`Preview failed: ${error.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImportJobs = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (endDate < startDate) {
      setError('End date must be after or equal to start date');
      return;
    }

    // Add warning for very large date ranges
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 90) {
      const confirmLargeRange = window.confirm(
        `You've selected a ${daysDiff}-day range. Large date ranges may take longer to process and could impact performance. Do you want to continue?`
      );
      if (!confirmLargeRange) {
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setImportResults(null);

    try {
      // Get tenant ID and city code from environment or user context
      const tenantId = process.env.REACT_APP_TENANT_ID || '1';
      const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';
      
      const response = await fetch(`${apiBase}/api/admin/hcp/import-jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          tenantId,
          cityCode
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to import jobs');
      }

      if (data.success) {
        setImportResults(data.results);
        setSuccess(data.message || 'Jobs imported successfully');
        // Refresh stats after import
        fetchImportStats();
        // Clear preview data since it's now outdated
        setPreviewData(null);
      } else {
        throw new Error(data.error || 'Failed to import jobs');
      }
    } catch (error) {
      setError(`Import failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      imported: { color: 'success', icon: <SuccessIcon fontSize="small" /> },
      updated: { color: 'info', icon: <SuccessIcon fontSize="small" /> },
      skipped: { color: 'warning', icon: <WarningIcon fontSize="small" /> },
      deleted: { color: 'secondary', icon: <ErrorIcon fontSize="small" /> },
      error: { color: 'error', icon: <ErrorIcon fontSize="small" /> }
    };

    const config = statusConfig[status] || { color: 'default', icon: null };

    return (
      <Chip 
        label={status}
        color={config.color}
        size="small"
        icon={config.icon}
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <ImportIcon color="primary" />
          HousecallPro Job Import Manager
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Import Statistics */}
          {importStats && importStats !== null && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <StatsIcon color="primary" />
                    <Typography variant="h6">Import Statistics</Typography>
                    <Tooltip title="Refresh Statistics">
                      <IconButton size="small" onClick={fetchImportStats}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Total Imported
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {importStats.totalImported}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Last Import
                      </Typography>
                      <Typography variant="body1">
                        {importStats.lastImportDate 
                          ? formatDate(importStats.lastImportDate)
                          : 'Never'
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Status Breakdown
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {Object.entries(importStats.byStatus || {}).map(([status, count]) => (
                          <Chip 
                            key={status}
                            label={`${status}: ${count}`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Date Range Selection */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Select Import Date Range
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose any date range to import jobs from Housecall Pro. You can select past, present, or future dates.
              </Typography>
              
              {/* Import Tips */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Import Tips:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>Past dates:</strong> Import historical job data and completed work
                  <br />
                  • <strong>Current dates:</strong> Import today's and upcoming scheduled jobs
                  <br />
                  • <strong>Future dates:</strong> Import scheduled jobs for planning and resource allocation
                  <br />
                  • <strong>Performance:</strong> Smaller date ranges (1-30 days) import faster than large ranges
                </Typography>
              </Alert>
              
              {/* Quick Date Presets */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Quick presets:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      setStartDate(today);
                      setEndDate(today);
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                      setStartDate(weekAgo);
                      setEndDate(today);
                    }}
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                      setStartDate(monthAgo);
                      setEndDate(today);
                    }}
                  >
                    Last 30 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
                      setStartDate(today);
                      setEndDate(nextWeek);
                    }}
                  >
                    Next 7 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const nextMonth = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
                      setStartDate(today);
                      setEndDate(nextMonth);
                    }}
                  >
                    Next 30 Days
                  </Button>
                </Box>
              </Box>
              
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      // Remove maxDate restriction to allow any date
                      // maxDate={new Date()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      // Remove maxDate restriction to allow any date
                      // maxDate={new Date()}
                      minDate={startDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={handlePreviewImport}
                        disabled={previewLoading || loading}
                        fullWidth
                      >
                        Preview
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<ImportIcon />}
                        onClick={handleImportJobs}
                        disabled={loading || previewLoading}
                        fullWidth
                      >
                        Import
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </LocalizationProvider>
              
              {/* Date validation message */}
              {startDate && endDate && startDate > endDate && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Start date should be before or equal to end date.
                </Alert>
              )}
              
              {/* Date range info */}
              {startDate && endDate && startDate <= endDate && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Selected Range:</strong> {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}
                    {startDate > new Date() && (
                      <span style={{ color: 'orange' }}> (Future dates selected)</span>
                    )}
                    {endDate < new Date() && (
                      <span style={{ color: 'blue' }}> (Past dates selected)</span>
                    )}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Loading */}
          {(loading || previewLoading) && (
            <Grid item xs={12}>
              <LinearProgress />
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                {loading ? 'Importing jobs...' : 'Loading preview...'}
              </Typography>
            </Grid>
          )}

          {/* Messages */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          {/* Preview Results */}
          {previewData && (
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Import Preview - {previewData.totalJobs} jobs found
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Total Jobs
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {previewData.totalJobs}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        New Jobs
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {previewData.newJobs || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Updates
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {previewData.jobsToUpdate || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        No Changes
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {previewData.noChangeJobs || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Invalid
                      </Typography>
                      <Typography variant="h4" color="error.main">
                        {previewData.invalidJobs || 0}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Employee Assignment Summary */}
                  {previewData.employeeAssignments && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Employee Assignment Preview
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="textSecondary">
                              Total Employees
                            </Typography>
                            <Typography variant="h5" color="primary">
                              {previewData.employeeAssignments.totalEmployees || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="textSecondary">
                              Will Assign
                            </Typography>
                            <Typography variant="h5" color="success.main">
                              {previewData.employeeAssignments.willAssign || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="textSecondary">
                              Won't Match
                            </Typography>
                            <Typography variant="h5" color="warning.main">
                              {previewData.employeeAssignments.willNotMatch || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </>
                  )}
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>HCP Job ID</TableCell>
                          <TableCell>Job Number</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Scheduled</TableCell>
                          <TableCell>Customer</TableCell>
                          <TableCell>Address</TableCell>
                          <TableCell>Action</TableCell>
                          <TableCell>Employee Assignments</TableCell>
                          <TableCell>Schedule Change</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.jobs.slice(0, 10).map((job) => {
                          // Determine action chip color and label
                          const getActionChip = (actionType) => {
                            switch(actionType) {
                              case 'create':
                                return <Chip label="Create New" color="success" size="small" />;
                              case 'update':
                                return <Chip label="Update" color="info" size="small" />;
                              case 'no_change':
                                return <Chip label="No Changes" color="default" size="small" />;
                              case 'invalid':
                                return <Chip label="Invalid" color="error" size="small" />;
                              default:
                                return <Chip label="Unknown" color="default" size="small" />;
                            }
                          };

                          return (
                            <TableRow key={job.hcpJobId}>
                              <TableCell>{job.hcpJobId}</TableCell>
                              <TableCell>{job.hcpJobNumber || '-'}</TableCell>
                              <TableCell>{job.status}</TableCell>
                              <TableCell>
                                {job.scheduledStart ? formatDate(job.scheduledStart) : '-'}
                              </TableCell>
                              <TableCell>{job.customerName || '-'}</TableCell>
                              <TableCell>{job.address || '-'}</TableCell>
                              <TableCell>
                                {getActionChip(job.actionType)}
                              </TableCell>
                              <TableCell>
                                {job.employeeAssignment ? (
                                  <Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                      {job.employeeAssignment.willAssign > 0 && (
                                        <Chip
                                          label={`${job.employeeAssignment.willAssign} Assigned`}
                                          color="success"
                                          size="small"
                                        />
                                      )}
                                      {job.employeeAssignment.willNotMatch > 0 && (
                                        <Chip
                                          label={`${job.employeeAssignment.willNotMatch} Unmatched`}
                                          color="warning"
                                          size="small"
                                        />
                                      )}
                                    </Box>
                                    {job.employeeAssignment.matchedEmployees && job.employeeAssignment.matchedEmployees.length > 0 && (
                                      <Typography variant="caption" color="success.main" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                        ✓ {job.employeeAssignment.matchedEmployees.map(e => `${e.name} (${e.email}) - ${e.role}`).join(', ')}
                                      </Typography>
                                    )}
                                    {job.employeeAssignment.unmatchedEmployees && job.employeeAssignment.unmatchedEmployees.length > 0 && (
                                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                        ✗ {job.employeeAssignment.unmatchedEmployees.join(', ')}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    No employees
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {job.scheduleChange === 'schedule_updated' ? (
                                  <Box>
                                    <Typography variant="caption" color="info.main">
                                      Schedule Updated
                                    </Typography>
                                    {job.oldSchedule && job.newSchedule && (
                                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                                        <div>From: {new Date(job.oldSchedule).toLocaleString()}</div>
                                        <div>To: {new Date(job.newSchedule).toLocaleString()}</div>
                                      </Box>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {previewData.jobs.length > 10 && (
                    <Typography variant="body2" sx={{ mt: 1 }} color="textSecondary">
                      Showing first 10 of {previewData.jobs.length} jobs
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* Import Results */}
          {importResults && (
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Import Results - {importResults.summary.totalFetched} jobs processed
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Total Fetched
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {importResults.summary.totalFetched}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Imported
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {importResults.summary.imported}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Updated
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {importResults.summary.updated || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Skipped
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {importResults.summary.skipped}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Deleted
                      </Typography>
                      <Typography variant="h4" color="secondary.main">
                        {importResults.summary.deleted || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Errors
                      </Typography>
                      <Typography variant="h4" color="error.main">
                        {importResults.summary.errors}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Employee Assignment Results */}
                  {importResults.summary.employeeAssignments && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Employee Assignment Results
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="textSecondary">
                              Total Assigned
                            </Typography>
                            <Typography variant="h5" color="success.main">
                              {importResults.summary.employeeAssignments.totalAssigned || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="textSecondary">
                              Jobs with Assignments
                            </Typography>
                            <Typography variant="h5" color="primary">
                              {importResults.summary.employeeAssignments.jobsWithAssignments || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="textSecondary">
                              Unmatched Employees
                            </Typography>
                            <Typography variant="h5" color="warning.main">
                              {importResults.summary.employeeAssignments.totalUnmatched || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>HCP Job ID</TableCell>
                          <TableCell>Local Job ID</TableCell>
                          <TableCell>Job Number</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Employee Assignments</TableCell>
                          <TableCell>Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResults.details.map((detail, index) => (
                          <TableRow key={index}>
                            <TableCell>{detail.hcpJobId}</TableCell>
                            <TableCell>{detail.localJobId || '-'}</TableCell>
                            <TableCell>{detail.jobNumber || '-'}</TableCell>
                            <TableCell>{getStatusChip(detail.status)}</TableCell>
                            <TableCell>
                              {detail.employeeAssignment ? (
                                <Box>
                                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                    {detail.employeeAssignment.assigned > 0 && (
                                      <Chip
                                        label={`${detail.employeeAssignment.assigned} Assigned`}
                                        color="success"
                                        size="small"
                                      />
                                    )}
                                    {detail.employeeAssignment.unmatched > 0 && (
                                      <Chip
                                        label={`${detail.employeeAssignment.unmatched} Unmatched`}
                                        color="warning"
                                        size="small"
                                      />
                                    )}
                                  </Box>
                                  {detail.employeeAssignment.unmatchedNames && detail.employeeAssignment.unmatchedNames.length > 0 && (
                                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                      ✗ {detail.employeeAssignment.unmatchedNames.join(', ')}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{detail.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HCPJobImportManager;